/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as _ from 'lodash';

import { PickerHandler, PickerContainer, PickerList, PickerGroup, PickerItem } from './styles';
import { useMouseWheel } from './useMouseWheel';

export interface DataPickerModel {
  textAlign?: 'start' | 'center' | 'end' | 'justify' | 'left' | 'right' | 'nowrap' | 'wrap';
  weight?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  className?: string;
  onClick: (index: number, currentIndex: number) => void;
  currentIndex?: number;
  list: string[];
  divider?: boolean;
  text?: string;
}

interface Props {
  data: DataPickerModel[];
  initialValue: string;
  change: (value: string) => void;
}

const DataPicker = ({ data, initialValue, change }: Props) => {
  const { delta, wheeling, position } = useMouseWheel();
  const [wheeled, setWheeled] = useState(false);

  const ITEM_PER_DEGREE = 24;
  const isTouchable: boolean = 'ontouchstart' in window;
  const groupDiv = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [groups, setGroups] = useState(data);
  const [initialized, setInitialized] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [value, setValue] = useState('');
  let _value = '';
  const [isDragging, setDragging] = useState(false);
  const [currentIdx, setCurrentIdx] = useState<number[]>([]);
  let _currentIdx;
  const [groupList, setGroupList] = useState<any[]>([]);
  let _groupIdx;
  let _isMouseDown;
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [startPageY, setStartPageY] = useState(null);
  let _startPageY;

  const refresh = (): void => {
    if (!initialized) {
      setInitialized(true);
      eventsRegister();
      window.removeEventListener('resize', bindEvents.bind(this));
      window.addEventListener('resize', bindEvents.bind(this));
      if (groups)
        groups.forEach((group, idx) => {
          setGroupData(idx, group);
        });
      getGroupList();
    }
    updateList();
  };

  const findIndexList = (value: string): number[] => {
    const arr = String(value).split(' ');
    const res = groups ? groups.map((item, idx) => item.list.findIndex((f) => f === arr[idx])) : [];
    return res;
  };

  const updateList = useCallback((): void => {
    //let res: number[] = [];
    //if (initialValue !== '') {
    const res = findIndexList(initialValue);
    //}
    if (!_.isEqual(currentIdx, res)) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _currentIdx = res;
      setCurrentIdx(res);
    }
  }, [currentIdx, setCurrentIdx, findIndexList, initialValue]);

  const getInitialCurrentIdx = useCallback((): number[] => {
    if (!groups || groups.length === 0) return [];
    const result = groups.map((item: DataPickerModel) => {
      const iCI = item.currentIndex;
      if (typeof iCI === 'number' && iCI >= 0 && item.list && item.list.length && iCI <= item.list.length - 1) {
        return Math.round(iCI);
      }
      return 0;
    });
    return result;
  }, [groups]);

  const setGroupData = useCallback(
    (gIndex: number, groupData: any): void => {
      if (_.isEmpty(_currentIdx)) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _currentIdx = getInitialCurrentIdx();
        setCurrentIdx(_currentIdx);
      }
      const auxGrp = groups;
      if (auxGrp) {
        auxGrp[gIndex] = groupData;
        setGroups(auxGrp);
        const iCI = groupData.currentIndex;
        let movedIndex = 0;
        if (
          typeof iCI === 'number' &&
          iCI >= 0 &&
          groupData.list &&
          groupData.list.length &&
          iCI <= groupData.list.length - 1
        ) {
          movedIndex = Math.round(iCI);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _currentIdx = currentIdx;
        _currentIdx[gIndex] = movedIndex;
        setCurrentIdx(_currentIdx);
      }
    },
    [currentIdx, getInitialCurrentIdx, groups, setGroups],
  );

  const getGroupList = useCallback((): void => {
    if (groupDiv.current) {
      const arr = (groupDiv.current as HTMLDivElement).childNodes;
      const aux: any[] = groupList || [];
      if (arr && arr.length > 0) {
        arr.forEach((item, index) => {
          aux[index] = (item as Element).getBoundingClientRect();
        });
      }
      setGroupList(aux);
    }
  }, [groupList, setGroupList]);

  const bindEvents = useCallback((): void => {
    if (timeoutId) clearTimeout(timeoutId);
    setTimeoutId(window.setTimeout(() => getGroupList(), 200));
  }, [timeoutId, getGroupList]);

  const getTouchInfo = useCallback(
    (ev: any): any => (isTouchable ? ev.changedTouches[0] || ev.touches[0] : ev),
    [isTouchable],
  );

  const getGroupIdxMousePosition = useCallback(
    (posX: number): number => {
      for (let i = 0; i < groupList.length; i++) {
        const item = groupList[i];
        if (item.left < posX && posX < item.right) return i;
      }
      return -1;
    },
    [groupList],
  );

  const getGroupIdxBelongsEvent = useCallback(
    (ev: Event): number => {
      const touchInfo = getTouchInfo(ev);
      return getGroupIdxMousePosition(touchInfo.pageX);
    },
    [getTouchInfo, getGroupIdxMousePosition],
  );

  const setCurrentIdxOnScroll = useCallback((): void => {
    let gIndex = _groupIdx;
    if (_groupIdx === null || _groupIdx === undefined) {
      gIndex = getGroupIdxMousePosition(position.x);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _groupIdx = gIndex;
    }
    if (gIndex === null && groups[gIndex] && (groups[gIndex].divider || !groups[gIndex].list)) return;
    const moveCount = delta.y / 1200;
    if (currentIdx && currentIdx[gIndex] !== undefined) {
      const _currentIdx = currentIdx;
      const movedIndex = _currentIdx[gIndex] + moveCount;
      _currentIdx[gIndex] = movedIndex;
      setCurrentIdx(_currentIdx);
    }
  }, [delta.y, position.x, getGroupIdxMousePosition, getTouchInfo, groups, currentIdx, setCurrentIdx]);

  useEffect(() => {
    const correctionAfterScroll = (gIndex, _currentIdx) => {
      if (
        groups === undefined ||
        groups[gIndex] === undefined ||
        groups[gIndex].list === undefined ||
        gIndex === undefined
      )
        return;
      if (
        typeof gIndex === 'number' &&
        groups[gIndex] !== undefined &&
        (groups[gIndex].divider === undefined || !groups[gIndex].divider) &&
        groups[gIndex].list.length > 0
      ) {
        const unsafeGroupIndex = _currentIdx[gIndex];
        let movedIndex = unsafeGroupIndex;
        if (unsafeGroupIndex > groups[gIndex].list.length - 1) {
          movedIndex = groups[gIndex].list.length - 1;
        } else if (unsafeGroupIndex < 0) {
          movedIndex = 0;
        }
        movedIndex = Math.round(movedIndex);
        _currentIdx[gIndex] = movedIndex;
        setCurrentIdx(_currentIdx);
        const val: any[] = [];
        _currentIdx.forEach((item, idx) => {
          val.push(groups[idx].list[item]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _value = val.join(' ');
        setCurrentIdx(_currentIdx);
        setValue(_value);
        emitChange();
      }
    };
    if (wheeling) {
      setCurrentIdxOnScroll();
      if (!wheeled) setWheeled(true);
    } else {
      if (wheeled && currentIdx) {
        for (let i = 0; i < currentIdx.length; i++) {
          correctionAfterScroll(i, currentIdx);
        }
        setWheeled(false);
      }
    }
  }, [wheeling, position, setCurrentIdxOnScroll, currentIdx, wheeled]);

  const setCurrentIdxOnMove = useCallback(
    (ev: Event): void => {
      const touchInfo = getTouchInfo(ev);
      let gIndex = _groupIdx;

      if (_groupIdx === null || _groupIdx === undefined) {
        gIndex = getGroupIdxBelongsEvent(ev);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _groupIdx = gIndex;
      }
      if (gIndex === null && groups[gIndex] && (groups[gIndex].divider || !groups[gIndex].list)) return;
      const moveCount = (_startPageY - touchInfo.pageY) / 32;
      const movedIndex = _currentIdx[gIndex] + moveCount;
      _currentIdx[gIndex] = movedIndex;
      setCurrentIdx(_currentIdx);
      setStartPageY(touchInfo.pageY);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _startPageY = touchInfo.pageY;
    },
    [getTouchInfo, groups, startPageY, currentIdx, getGroupIdxBelongsEvent, setStartPageY, setCurrentIdx],
  );

  const emitChange = (): void => change(_value);

  const updateValue = useCallback((): void => {
    const val: any[] = [];
    _currentIdx.forEach((item, idx) => {
      val.push(groups[idx].list[item]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    _value = val.join(' ');
    setCurrentIdx(_currentIdx);
    setValue(_value);
    emitChange();
  }, [currentIdx, emitChange, setValue]);

  const correctionCurrentIdx = useCallback(
    (ev: Event, gIndex: number): void => {
      if (typeof gIndex === 'number' && !groups[gIndex].divider && groups[gIndex].list.length > 0) {
        const unsafeGroupIndex = _currentIdx[gIndex];
        let movedIndex = unsafeGroupIndex;
        if (unsafeGroupIndex > groups[gIndex].list.length - 1) {
          movedIndex = groups[gIndex].list.length - 1;
        } else if (unsafeGroupIndex < 0) {
          movedIndex = 0;
        }
        movedIndex = Math.round(movedIndex);
        _currentIdx[gIndex] = movedIndex;
        setCurrentIdx(_currentIdx);
        updateValue();
      }
    },
    [_currentIdx, groups, updateValue],
  );

  const correctionAfterDragging = useCallback(
    (ev: Event): void => {
      for (let i = 0; i < currentIdx.length; i++) {
        correctionCurrentIdx(ev, i);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _startPageY = null;
      setStartPageY(_startPageY);
    },
    [correctionCurrentIdx],
  );

  const triggerMiddleLayerGroupClick = (gIndex: number): void => {
    if (typeof gIndex === 'number' && typeof groups[gIndex].onClick === 'function') {
      groups[gIndex].onClick(gIndex, currentIdx[gIndex]);
    }
  };

  const triggerAboveLayerClick = (ev: Event, gIndex: number): void => {
    const movedIndex = currentIdx[gIndex] + 1;
    const aux = currentIdx;
    aux[gIndex] = movedIndex;
    setCurrentIdx(aux);
    correctionCurrentIdx(ev, gIndex);
  };

  const triggerMiddleLayerClick = (ev: Event, gIndex: number): void => {
    triggerMiddleLayerGroupClick(gIndex);
    correctionCurrentIdx(ev, gIndex);
  };

  const triggerBelowLayerClick = (ev: Event, gIndex: number): void => {
    const movedIndex = currentIdx[gIndex] - 1;
    const aux = currentIdx;
    aux[gIndex] = movedIndex;
    setCurrentIdx(aux);
    correctionCurrentIdx(ev, gIndex);
  };

  const handleEventClick = useCallback(
    (ev: any): void => {
      const gIndex = getGroupIdxBelongsEvent(ev);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _groupIdx = gIndex;
      if (gIndex && gIndex > -1)
        switch (ev.target.dataset.type) {
          case 'top':
            triggerAboveLayerClick(ev, gIndex);
            break;
          case 'middle':
            triggerMiddleLayerClick(ev, gIndex);
            break;
          case 'bottom':
            triggerBelowLayerClick(ev, gIndex);
            break;
          default:
        }
    },
    [getGroupIdxBelongsEvent, triggerAboveLayerClick, triggerBelowLayerClick, triggerMiddleLayerClick],
  );

  const handleStart = useCallback((ev: Event): void => {
    if (ev.cancelable) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    const touchInfo = getTouchInfo(ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    _startPageY = touchInfo.pageY;
    setStartPageY(touchInfo.pageY);
    if (!isTouchable) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _isMouseDown = true;
    }
  }, []);

  const handleMove = useCallback(
    (ev: Event): void => {
      if (ev.cancelable) {
        ev.preventDefault();
        ev.stopPropagation();
      }
      if (isTouchable || _isMouseDown) {
        setDragging(true);
        setCurrentIdxOnMove(ev);
      }
    },
    [isTouchable, _isMouseDown, setCurrentIdxOnMove],
  );

  const handleEnd = useCallback((ev: Event): void => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!isDragging) handleEventClick(ev);
    setDragging(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    _isMouseDown = false;
    correctionAfterDragging(ev);
  }, []);

  const handleCancel = useCallback(
    (ev: Event): void => {
      ev.preventDefault();
      ev.stopPropagation();
      if (isTouchable || _isMouseDown) {
        correctionAfterDragging(ev);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _isMouseDown = false;
        setDragging(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        _groupIdx = null;
      }
    },
    [correctionAfterDragging, isTouchable],
  );

  const addEventsForElement = useCallback(
    (el: HTMLElement | null): void => {
      const _ = isTouchable;
      const eventHandlerList = [
        { name: _ ? 'touchstart' : 'mousedown', handler: handleStart },
        { name: _ ? 'touchmove' : 'mousemove', handler: handleMove },
        { name: _ ? 'touchend' : 'mouseup', handler: handleEnd },
        {
          name: _ ? 'touchcancel' : 'mouseleave',
          handler: handleCancel,
        },
      ];
      if (el)
        eventHandlerList.forEach((item) => {
          el.removeEventListener(item.name, item.handler, false);
          el.addEventListener(item.name, item.handler, false);
        });
    },
    [handleCancel, handleEnd, handleMove, handleStart, isTouchable],
  );

  const removeEventsForElement = useCallback(
    (el: HTMLElement | null): void => {
      const _ = isTouchable;
      const eventHandlerList = [
        { name: _ ? 'touchstart' : 'mousedown', handler: handleStart },
        { name: _ ? 'touchmove' : 'mousemove', handler: handleMove },
        { name: _ ? 'touchend' : 'mouseup', handler: handleEnd },
        {
          name: _ ? 'touchcancel' : 'mouseleave',
          handler: handleCancel,
        },
      ];
      if (el) eventHandlerList.forEach((item) => el.removeEventListener(item.name, item.handler, false));
    },
    [handleCancel, handleEnd, handleMove, handleStart, isTouchable],
  );

  const eventsRegister = (): void => {
    addEventsForElement(pickerRef.current);
  };

  const eventsRemove = (): void => {
    removeEventsForElement(pickerRef.current);
  };

  const isCurrentItem = (gIndex: number, iIndex: number): boolean => currentIdx[gIndex] === iIndex;

  const getGroupClass = (gIndex: number): string => {
    const group = groups[gIndex];
    const defaultWeightClass = `weight-${group.weight || 1}`;
    const groupClass = [defaultWeightClass];
    if (group.className) groupClass.push(group.className);
    return groupClass.join(' ');
  };

  const getItemClass = (gIndex: number, iIndex: number | null, isDivider = false): string => {
    const group = groups[gIndex];
    const itemClass: any[] = [];
    if (!isDivider && iIndex && isCurrentItem(gIndex, iIndex)) itemClass.push('smooth-item-selected');
    if (group.textAlign) itemClass.push(`text-${group.textAlign}`);
    return itemClass.join(' ');
  };

  const getItemStyle = useCallback(
    (gIndex: number, iIndex: number): any => {
      const gapCount = currentIdx[gIndex] - iIndex;

      if (Math.abs(gapCount) < 90 / ITEM_PER_DEGREE) {
        const rotateStyle = {
          transform: `rotateX(${gapCount * ITEM_PER_DEGREE}deg) translate3d(0, 0, 5.625em)`,
          opacity: (1 - Math.abs(gapCount) / (90 / ITEM_PER_DEGREE)).toString(),
        };
        if (!isDragging) rotateStyle['transition'] = 'transform 150ms ease-out';
        return rotateStyle;
      }
      const res =
        gapCount > 0
          ? { transform: 'rotateX(90deg) translate3d(0, 0, 5.625em)' }
          : { transform: 'rotateX(-90deg) translate3d(0, 0, 5.625em)' };

      return res;
    },
    [currentIdx, isDragging],
  );

  const showGroup = (group: DataPickerModel, gIndex: number) => {
    if (group)
      return group.list.map((item, iIndex) => (
        <PickerItem
          key={`item-${gIndex}-${iIndex}`}
          className={`divider ${getItemClass(gIndex, iIndex)}`}
          style={getItemStyle(gIndex, iIndex)}
        >
          {item}
        </PickerItem>
      ));
    return null;
  };

  useEffect(() => {
    const init = () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      _currentIdx = getInitialCurrentIdx();
      setCurrentIdx(_currentIdx);
      const len = groups ? groups.length : 0;
      setGroupList(new Array(len));
      setTimeout(() => refresh(), 500);
    };
    init();
    return () => {
      setInitialized(false);
      eventsRemove();
      window.removeEventListener('resize', bindEvents.bind(this));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PickerContainer ref={groupDiv}>
        {groups &&
          groups.length > 0 &&
          groups.map((group, gIndex) => (
            <PickerGroup key={`group-${gIndex}`} className={`${getGroupClass(gIndex)}`}>
              <PickerList key={`list-${gIndex}`}>
                {group.divider ? (
                  <PickerItem className={`divider ${getItemClass(gIndex, null, true)}`}>{group.text}</PickerItem>
                ) : (
                  showGroup(group, gIndex)
                )}
              </PickerList>
            </PickerGroup>
          ))}
        <PickerHandler ref={pickerRef}>
          <PickerHandler.Top data-type="top" />
          <PickerHandler.Middle data-type="middle" />
          <PickerHandler.Bottom data-type="bottom" />
        </PickerHandler>
      </PickerContainer>
    </>
  );
};

export default DataPicker;
